interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

interface ApiConfig {
  baseUrl: string;
}

interface ShopifyConfig {
  clientId: string;
  redirectUri: string;
}

export interface AppConfig {
  firebase: FirebaseConfig;
  api: ApiConfig;
  shopify: ShopifyConfig;
  featureFlags?: Record<string, boolean | string>;
}

class ConfigLoader {
  private config: AppConfig | null = null;
  private loadingPromise: Promise<AppConfig> | null = null;

  async loadConfig(): Promise<AppConfig> {
    if (this.config) {
      return this.config;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.fetchConfig();
    this.config = await this.loadingPromise;
    this.loadingPromise = null;

    return this.config;
  }

  private async fetchConfig(): Promise<AppConfig> {
    try {
      const response = await fetch("/config.json");

      if (!response.ok) {
        throw new Error(`Failed to load configuration: ${response.status} ${response.statusText}`);
      }

      const config = (await response.json()) as AppConfig;

      this.validateConfig(config);

      return config;
    } catch (error) {
      console.error("Error loading config.json:", error);
      throw new Error(
        "Failed to load configuration. Ensure config.json exists in the public folder."
      );
    }
  }

  private validateConfig(config: AppConfig): asserts config is AppConfig {
    if (!config || typeof config !== "object") {
      throw new Error("Invalid configuration: config must be an object");
    }

    if (!config.firebase || typeof config.firebase !== "object") {
      throw new Error("Invalid configuration: firebase section is required");
    }

    const { firebase } = config;

    if (!firebase.apiKey || typeof firebase.apiKey !== "string") {
      throw new Error("Invalid configuration: firebase.apiKey is required");
    }

    if (!firebase.authDomain || typeof firebase.authDomain !== "string") {
      throw new Error("Invalid configuration: firebase.authDomain is required");
    }

    if (!firebase.projectId || typeof firebase.projectId !== "string") {
      throw new Error("Invalid configuration: firebase.projectId is required");
    }

    if (!config.api || typeof config.api !== "object") {
      throw new Error("Invalid configuration: api section is required");
    }

    if (!config.api.baseUrl || typeof config.api.baseUrl !== "string") {
      throw new Error("Invalid configuration: api.baseUrl is required");
    }

    if (!config.shopify || typeof config.shopify !== "object") {
      throw new Error("Invalid configuration: shopify section is required");
    }

    if (!config.shopify.clientId || typeof config.shopify.clientId !== "string") {
      throw new Error("Invalid configuration: shopify.clientId is required");
    }
  }

  getConfig(): AppConfig | null {
    return this.config;
  }
}

export const configLoader = new ConfigLoader();
